import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DossierSettlement, DossierSettlementEvent } from '../../api/dossier';
import { IdText } from './IdText';
import { KIND, kindOf, missingInExact, money, stamp } from './money';
import './Money.scss';

/** A failure of the connector on the way between the two systems, from the dossier's moments. */
export interface FlowFailure {
    key: string;
    at?: string | null;
    lastAt?: string | null;
    count: number;
    /** What failed, as the rule names it: "Exact weigerde het afletteren". */
    title: string;
    /** Which object: "Betaling txn_…". */
    subject: string;
    /** The failure the screen is about, drawn heavier. */
    focus?: boolean;
}

type Row =
    | { type: 'event'; at?: string | null; dayOnly?: boolean; event: DossierSettlementEvent; openAfter?: number | null; unmatched?: boolean; explain?: boolean }
    | { type: 'failure'; at?: string | null; failure: FlowFailure };

const time = (iso?: string | null) => (iso ? new Date(iso).getTime() : Number.MAX_SAFE_INTEGER);

/**
 * Walks the events in order and works out what each side has open after each one, which is what makes
 * "afgeletterd", "deelbetaling" and "creditnota" visible: the number that changes, or does not.
 */
const rowsOf = (settlement: DossierSettlement, failures: FlowFailure[]): Row[] => {
    const total = settlement.total ?? 0;
    let chargebee = 0;
    let exact: number | null = null;

    const events: Row[] = settlement.events.map((event) => {
        const amount = event.amount ?? 0;

        switch (kindOf(event)) {
            case 'Invoice':
                chargebee = amount || total;
                return { type: 'event', at: event.at, dayOnly: event.dayOnly, event, openAfter: chargebee };
            case 'Payment':
            case 'CreditNote':
                chargebee = Math.max(0, chargebee - amount);
                return { type: 'event', at: event.at, dayOnly: event.dayOnly, event, openAfter: chargebee };
            case 'Chargeback':
                chargebee += amount;
                return { type: 'event', at: event.at, dayOnly: event.dayOnly, event, openAfter: chargebee };
            case 'SalesEntry':
                exact = total;
                return { type: 'event', at: event.at, dayOnly: event.dayOnly, event, openAfter: exact };
            case 'Matched':
            case 'PartlyMatched':
                exact = event.amount === null || event.amount === undefined ? (kindOf(event) === 'Matched' ? 0 : exact) : Math.max(0, (exact ?? total) - amount);
                return { type: 'event', at: event.at, dayOnly: event.dayOnly, event, openAfter: exact };
            case 'BankPayment':
            case 'CreditNoteBooked':
                // Money on the bank or a credit note in Exact changes nothing on the invoice until it is matched;
                // the server says which one no match accounts for.
                return { type: 'event', at: event.at, dayOnly: event.dayOnly, event, unmatched: !!event.unmatched };
            default:
                return { type: 'event', at: event.at, dayOnly: event.dayOnly, event };
        }
    });

    // What a kind of event does is said once, at its first appearance; after that the tooltip has it.
    const explained = new Set<string>();

    events.forEach((row) => {
        if (row.type === 'event' && !explained.has(kindOf(row.event))) {
            explained.add(kindOf(row.event));
            row.explain = true;
        }
    });

    const merged = [...events];

    // A failure goes after everything that happened before it; a day without a time counts where the server put it.
    failures.forEach((failure) => {
        const at = time(failure.at);
        const index = merged.findIndex((row) => time(row.type === 'event' ? row.event.sortAt ?? row.at : row.at) > at);

        merged.splice(index < 0 ? merged.length : index, 0, { type: 'failure', at: failure.at, failure });
    });

    return merged;
};

export interface IMoneyFlowProps {
    settlement: DossierSettlement;
    failures?: FlowFailure[];
    /** A reference to draw heavier: the payment or invoice the screen is about. */
    highlight?: string | null;
}

/**
 * What happened to the money of one invoice, in three lanes: Chargebee, what the connector carried
 * across, and Exact. Every event says what it does and what is open after it on its own side, so the
 * point where the two systems part is where the numbers stop matching.
 */
export const MoneyFlow = ({ settlement, failures = [], highlight }: IMoneyFlowProps) => {
    const rows = rowsOf(settlement, failures);

    return (
        <div className="flow" role="table" aria-label={`Geldstroom van factuur ${settlement.invoiceId}`}>
            <div className="flow__row flow__row--head" role="row">
                <span role="columnheader">Wanneer</span>
                <span role="columnheader" className="flow__lane-head flow__lane-head--chargebee">
                    Chargebee
                </span>
                <span role="columnheader" className="flow__lane-head flow__lane-head--connector">
                    <FontAwesomeIcon icon="robot" /> Connector
                </span>
                <span role="columnheader" className="flow__lane-head flow__lane-head--exact">
                    Exact
                </span>
            </div>

            {rows.map((row, index) =>
                row.type === 'failure' ? (
                    <FailureRow key={`f-${row.failure.key}`} failure={row.failure} />
                ) : (
                    <EventRow key={`e-${index}`} row={row} highlight={highlight} />
                )
            )}

            <div className="flow__row flow__row--now" role="row">
                <span className="flow__when" role="cell">
                    Nu
                </span>
                <span role="cell" className="flow__cell flow__cell--chargebee">
                    <NowPill amount={settlement.openInChargebee} />
                </span>
                <span role="cell" className="flow__cell flow__cell--connector" />
                <span role="cell" className="flow__cell flow__cell--exact">
                    {missingInExact(settlement) ? (
                        <span className="flow__now flow__now--missing">
                            <FontAwesomeIcon icon="triangle-exclamation" /> niet in Exact
                        </span>
                    ) : (
                        <NowPill amount={settlement.openInExact} />
                    )}
                </span>
            </div>
        </div>
    );
};

const NowPill = ({ amount }: { amount?: number | null }) =>
    amount === null || amount === undefined ? (
        <span className="flow__now flow__now--unknown">niet gelezen</span>
    ) : (
        <span className={`flow__now flow__now--${amount > 0.004 ? 'open' : 'done'}`}>
            <FontAwesomeIcon icon={amount > 0.004 ? 'circle-exclamation' : 'circle-check'} /> {amount > 0.004 ? `${money(amount)} open` : 'niets open'}
        </span>
    );

const EventRow = ({ row, highlight }: { row: Extract<Row, { type: 'event' }>; highlight?: string | null }) => {
    const { event } = row;
    const kind = KIND[kindOf(event)];
    const onChargebee = event.side === 'Chargebee';
    const focus = !!highlight && event.reference === highlight;
    const warn = event.warning || row.unmatched;

    const card = (
        <div className={`flow__card${warn ? ' flow__card--warn' : ''}${focus ? ' flow__card--focus' : ''}${event.undone ? ' flow__card--undone' : ''}`} title={kind.meaning}>
            <span className="flow__card-head">
                <FontAwesomeIcon icon={warn ? 'triangle-exclamation' : kind.icon} className="flow__card-icon" />
                <span className="flow__card-title">{event.title}</span>
                {event.undone && <span className="flow__undone">teruggedraaid</span>}
                {event.amount !== null && event.amount !== undefined && <span className="flow__amount">{money(event.amount)}</span>}
            </span>
            {(event.reference || event.note) && (
                <span className="flow__meta">
                    <IdText text={[event.reference, event.note].filter(Boolean).join(' · ')} />
                </span>
            )}
            {row.explain && kind.meaning && <span className="flow__meaning">{kind.meaning}</span>}
            {row.unmatched && (
                <span className="flow__alert">
                    {kindOf(event) === 'CreditNoteBooked' ? 'Staat in Exact, maar is nog niet tegen de factuur afgeletterd.' : 'Staat op de bank, maar is nog niet tegen de factuur afgeletterd.'}
                </span>
            )}
            {row.openAfter !== undefined && row.openAfter !== null && (
                <span className={`flow__after${row.openAfter > 0.004 ? ' flow__after--open' : ''}`}>open daarna: {money(row.openAfter)}</span>
            )}
        </div>
    );

    return (
        <div className={`flow__row${focus ? ' flow__row--focus' : ''}`} role="row">
            <span className="flow__when" role="cell" title={row.dayOnly ? 'Exact geeft hier alleen een datum, geen tijd' : undefined}>
                {stamp(row.at, row.dayOnly)}
                {row.dayOnly && <span className="flow__day-only">alleen datum</span>}
            </span>
            <span role="cell" className="flow__cell flow__cell--chargebee">
                {onChargebee && card}
            </span>
            <span role="cell" className="flow__cell flow__cell--connector">
                {event.byConnector && (
                    <span className={`flow__carry flow__carry--${onChargebee ? 'back' : 'over'}`}>
                        {onChargebee ? (
                            <>
                                <FontAwesomeIcon icon="arrow-left-long" /> uit Exact
                            </>
                        ) : (
                            <>
                                naar Exact <FontAwesomeIcon icon="arrow-right-long" />
                            </>
                        )}
                    </span>
                )}
            </span>
            <span role="cell" className="flow__cell flow__cell--exact">
                {!onChargebee && card}
            </span>
        </div>
    );
};

const FailureRow = ({ failure }: { failure: FlowFailure }) => (
    <div className={`flow__row flow__row--failure${failure.focus ? ' flow__row--focus' : ''}`} role="row">
        <span className="flow__when" role="cell">
            {stamp(failure.at)}
        </span>
        <span role="cell" className="flow__cell flow__cell--span">
            <span className="flow__failure">
                <span className="flow__card-head">
                    <FontAwesomeIcon icon="circle-xmark" className="flow__card-icon" />
                    <span className="flow__card-title">{failure.title}</span>
                </span>
                <span className="flow__meta">
                    <IdText text={failure.subject} />
                    {failure.count > 1 ? ` · ${failure.count} pogingen, laatste ${stamp(failure.lastAt)}` : ''}
                </span>
            </span>
        </span>
    </div>
);
